/**
 * IndexedDB cache for raw GSX block bytes, keyed by an opaque string.
 *
 * Caller picks the key (typically `jobId` or `${uploadId}:${paramHash}`).
 * Since these are content-addressable identifiers, cache invalidation is
 * automatic — no ETag / HEAD round-trip needed.
 *
 * All operations are best-effort: any DB error (quota exceeded, private
 * mode, etc.) is logged and the caller falls back to network.
 */
const DB_NAME = "sc-quick3d-gsplat";
const DB_VERSION = 1;
const STORE = "splatBlocks";

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn("[cache] open IDB failed:", req.error);
      resolve(null);
    };
  });
  return dbPromise;
}

export async function getCachedBytes(key: string): Promise<Uint8Array | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        const v = req.result as ArrayBuffer | undefined;
        resolve(v ? new Uint8Array(v) : null);
      };
      req.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn("[cache] get failed:", req.error);
        resolve(null);
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[cache] get threw:", err);
      resolve(null);
    }
  });
}

export async function putCachedBytes(key: string, bytes: Uint8Array): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const buf = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      );
      store.put(buf, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn("[cache] put failed:", tx.error);
        resolve();
      };
      tx.onabort = () => {
        // eslint-disable-next-line no-console
        console.warn("[cache] put aborted:", tx.error);
        resolve();
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[cache] put threw:", err);
      resolve();
    }
  });
}

export async function deleteCached(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function listCached(): Promise<string[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAllKeys();
      req.onsuccess = () => resolve((req.result as string[]) ?? []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}
